import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { formatApiError } from "../../lib/AuthContext";
import { PhoneField } from "../PhoneField";

const EXPERIENCES = ["beginner", "some_experience", "advanced"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const TIMES = ["morning", "afternoon", "evening"];

function PTAuthField({ label, children }) {
  return <div><label className="chip text-[#A3A3A3]">{label}</label>{children}</div>;
}

function PreferenceChips({ values, selected, onToggle, testid }) {
  const { t } = useT();
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {values.map((value) => (
        <button key={value} type="button" onClick={() => onToggle(value)}
          className="chip px-3 py-2 border transition-colors"
          style={selected.includes(value) ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
          data-testid={`${testid}-${value}`}>
          {t(value.startsWith("mon") || value.startsWith("tue") || value.startsWith("wed") || value.startsWith("thu") || value.startsWith("fri") || value.startsWith("sat") || value.startsWith("sun") ? `pt.day.${value}` : `pt.time.${value}`)}
        </button>
      ))}
    </div>
  );
}

export default function PTAuthModal({ onClose, onAuthed, signIn, register, initialMode = "register", packages = [], initialPref = "" }) {
  const { t, lang } = useT();
  const [mode, setMode] = useState(initialMode);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", dob: "",
    password: "", password2: "", boxing_experience: "", health_notes: "",
    emergency_name: "", emergency_phone: "", package_preference: initialPref,
    preferred_days: [], preferred_times: [], accept_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggle = (k, value) => setF((p) => ({ ...p, [k]: p[k].includes(value) ? p[k].filter((x) => x !== value) : [...p[k], value] }));

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      if (f.password !== f.password2) { toast.error(t("pt.auth.passwordMismatch")); return; }
      if (!f.phone.trim() || !f.emergency_name.trim() || !f.emergency_phone.trim()) { toast.error(t("pt.auth.requiredMissing")); return; }
      if (!f.accept_terms) { toast.error(t("pt.auth.acceptTerms")); return; }
    }
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: f.email.trim() });
        setForgotSent(true);
      } else if (mode === "login") {
        await signIn(f.email.trim(), f.password, true);
        toast.success(t("pt.auth.loggedIn"));
        onAuthed();
      } else {
        await register({
          first_name: f.first_name,
          last_name: f.last_name,
          email: f.email.trim(),
          phone: f.phone,
          dob: f.dob,
          password: f.password,
          accept_terms: true,
          pt_signup: true,
          preferred_language: lang,
          boxing_experience: f.boxing_experience,
          health_notes: f.health_notes,
          emergency_contact: { name: f.emergency_name, phone: f.emergency_phone, relation: "not_specified" },
          package_preference: f.package_preference,
          preferred_training_days: f.preferred_days,
          preferred_training_times: f.preferred_times,
        });
        toast.success(t("pt.auth.registered"));
        onAuthed();
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("pt.auth.failed"));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-lg w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pt-auth-modal">
        <div className="chip text-[#A3A3A3]">// {t("pt.auth.label").toUpperCase()}</div>
        <div className="font-display uppercase text-3xl mt-1 mb-6" data-testid="pt-auth-title">
          {mode === "login" ? t("pt.auth.loginTitle") : mode === "register" ? t("pt.auth.registerTitle") : t("pt.auth.forgotTitle")}
        </div>

        {forgotSent ? (
          <div className="chip text-[#DDD6CB]" data-testid="pt-forgot-sent">{t("pt.auth.resetSent")}</div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <PTAuthField label={`${t("pt.common.firstName")}*`}><input required value={f.first_name} onChange={set("first_name")} className="editorial-input dark" data-testid="pt-reg-first" /></PTAuthField>
                  <PTAuthField label={`${t("pt.common.lastName")}*`}><input required value={f.last_name} onChange={set("last_name")} className="editorial-input dark" data-testid="pt-reg-last" /></PTAuthField>
                </div>
              </>
            )}
            <PTAuthField label={`${t("pt.common.email")}*`}><input required type="email" value={f.email} onChange={set("email")} className="editorial-input dark" data-testid={mode === "register" ? "pt-reg-email" : "pt-auth-email"} /></PTAuthField>
            {mode === "register" && (
              <>
                <PTAuthField label={`${t("pt.common.phone")}*`}><PhoneField value={f.phone} onChange={(v) => setF((p) => ({ ...p, phone: v }))} dark testid="pt-reg-phone" /></PTAuthField>
                <PTAuthField label={`${t("pt.common.dob")}*`}><input required type="date" value={f.dob} onChange={set("dob")} className="editorial-input dark" data-testid="pt-reg-dob" /></PTAuthField>
              </>
            )}
            {mode !== "forgot" && (
              <PTAuthField label={`${t("pt.common.password")}*${mode === "register" ? ` (${t("pt.common.minChars")})` : ""}`}><input required type="password" minLength={mode === "register" ? 8 : undefined} value={f.password} onChange={set("password")} className="editorial-input dark" data-testid={mode === "register" ? "pt-reg-password" : "pt-auth-password"} /></PTAuthField>
            )}
            {mode === "register" && (
              <>
                <PTAuthField label={`${t("pt.common.confirmPassword")}*`}><input required type="password" minLength={8} value={f.password2} onChange={set("password2")} className="editorial-input dark" data-testid="pt-reg-password2" /></PTAuthField>
                <PTAuthField label={`${t("pt.auth.boxingExperience")}*`}>
                  <select required value={f.boxing_experience} onChange={set("boxing_experience")} className="editorial-input dark" data-testid="pt-reg-experience">
                    <option value="" className="bg-[#0A0A0A]">{t("pt.common.choose")}</option>
                    {EXPERIENCES.map((value) => <option key={value} value={value} className="bg-[#0A0A0A]">{t(`pt.auth.exp${value === "beginner" ? "Beginner" : value === "some_experience" ? "Some" : "Advanced"}`)}</option>)}
                  </select>
                </PTAuthField>
                <PTAuthField label={t("pt.auth.healthNotes")}>
                  <textarea rows={3} value={f.health_notes} onChange={set("health_notes")} placeholder={t("pt.auth.healthNotesPh")} className="editorial-input dark w-full" data-testid="pt-reg-health" />
                </PTAuthField>
                <div className="border border-[#F5F3F0]/15 p-4 space-y-3" data-testid="pt-reg-emergency">
                  <div className="chip text-[#4A4A4A]">— {t("pt.auth.emergencyContact").toUpperCase()}</div>
                  <PTAuthField label={`${t("pt.auth.emergencyName")}*`}><input required value={f.emergency_name} onChange={set("emergency_name")} className="editorial-input dark" data-testid="pt-reg-emergency-name" /></PTAuthField>
                  <PTAuthField label={`${t("pt.auth.emergencyPhone")}*`}><PhoneField value={f.emergency_phone} onChange={(v) => setF((p) => ({ ...p, emergency_phone: v }))} dark testid="pt-reg-emergency-phone" /></PTAuthField>
                </div>
                <PTAuthField label={t("pt.auth.packagePreference")}>
                  <select value={f.package_preference} onChange={set("package_preference")} className="editorial-input dark" data-testid="pt-reg-package">
                    <option value="" className="bg-[#0A0A0A]">{t("pt.auth.notSure")}</option>
                    {packages.map((p) => (
                      <option key={p.key} value={p.key} className="bg-[#0A0A0A]">{lang === "en" ? (p.name_en || p.name) : p.name} — {p.sessions} {lang === "en" ? (p.sessions > 1 ? "sessions" : "session") : (p.sessions > 1 ? "sessies" : "sessie")} · €{p.price}</option>
                    ))}
                  </select>
                </PTAuthField>
                <PTAuthField label={t("pt.auth.preferredDays")}><PreferenceChips values={DAYS} selected={f.preferred_days} onToggle={(v) => toggle("preferred_days", v)} testid="pt-reg-days" /></PTAuthField>
                <PTAuthField label={t("pt.auth.preferredTimes")}><PreferenceChips values={TIMES} selected={f.preferred_times} onToggle={(v) => toggle("preferred_times", v)} testid="pt-reg-times" /></PTAuthField>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="pt-reg-terms-wrap">
                  <input type="checkbox" checked={f.accept_terms} onChange={set("accept_terms")} className="w-4 h-4 mt-0.5 accent-[#D4A93A]" data-testid="pt-reg-terms" />
                  <span className="chip text-[#DDD6CB] leading-relaxed normal-case tracking-normal">
                    {t("pt.auth.termsPre")}
                    <Link to="/voorwaarden" target="_blank" className="underline underline-offset-4" style={{ color: "var(--joh-accent)" }}>{t("pt.auth.terms")}</Link>
                    {t("pt.auth.termsAnd")}
                    <Link to="/privacy" target="_blank" className="underline underline-offset-4" style={{ color: "var(--joh-accent)" }}>{t("pt.auth.privacy")}</Link>*
                  </span>
                </label>
              </>
            )}
            <button type="submit" disabled={busy} className="w-full py-3.5 chip" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-auth-submit">
              {busy ? "…" : mode === "login" ? t("pt.auth.submitLogin") : mode === "register" ? t("pt.auth.submitRegister") : t("pt.auth.submitReset")}
            </button>
          </form>
        )}

        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
          {mode !== "login" && <button type="button" onClick={() => { setMode("login"); setForgotSent(false); }} className="chip text-[#A3A3A3] underline underline-offset-4" data-testid="pt-auth-to-login">{t("pt.auth.toLogin")}</button>}
          {mode === "login" && <button type="button" onClick={() => setMode("register")} className="chip text-[#A3A3A3] underline underline-offset-4" data-testid="pt-auth-to-register">{t("pt.auth.toRegister")}</button>}
          {mode === "login" && <button type="button" onClick={() => setMode("forgot")} className="chip text-[#4A4A4A] underline underline-offset-4" data-testid="pt-auth-to-forgot">{t("pt.auth.toForgot")}</button>}
        </div>
      </div>
    </div>
  );
}
